import React, { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, UploadCloud, CheckCircle2, XCircle, AlertTriangle, Users, Receipt, CreditCard, Wrench } from "lucide-react";
import { toast } from "sonner";

const ENTITIES = [
  { key: "customer", label: "Customers", icon: Users },
  { key: "invoice", label: "Invoices", icon: Receipt },
  { key: "payment", label: "Payments", icon: CreditCard },
  { key: "service_item", label: "Service Items", icon: Wrench },
];

const STATUS_CLS = {
  NOT_SYNCED: "bg-slate-100 text-slate-600",
  QUEUED: "bg-amber-100 text-amber-700",
  SYNCING: "bg-blue-100 text-blue-700",
  SYNCED: "bg-emerald-100 text-emerald-700",
  ERROR: "bg-rose-100 text-rose-700",
  NEEDS_REVIEW: "bg-orange-100 text-orange-700",
};
const STATUS_LABEL = { NOT_SYNCED: "Not Synced", QUEUED: "Queued", SYNCING: "Syncing", SYNCED: "Synced", ERROR: "Error", NEEDS_REVIEW: "Needs Review" };

function StatusBadge({ s }) {
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[s] || STATUS_CLS.NOT_SYNCED}`}>{STATUS_LABEL[s] || s}</span>;
}

export default function QuickBooksSync() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("customer");
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(() => api.get("/admin/quickbooks/summary").then(({ data }) => setSummary(data)).catch(() => setSummary(false)), []);
  const loadLog = useCallback(() => api.get("/admin/quickbooks/log").then(({ data }) => setLog(data)).catch(() => setLog([])), []);
  const loadRows = useCallback((type) => {
    setLoadingRows(true);
    api.get(`/admin/quickbooks/entities?type=${type}`).then(({ data }) => setRows(data)).catch(() => setRows([])).finally(() => setLoadingRows(false));
  }, []);

  useEffect(() => { loadSummary(); loadLog(); }, [loadSummary, loadLog]);
  useEffect(() => { loadRows(tab); }, [tab, loadRows]);

  const refresh = () => { loadSummary(); loadRows(tab); loadLog(); };

  const syncOne = async (entity_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/quickbooks/sync", { entity_type: tab, entity_ids: [entity_id] });
      toast[data.connected ? "success" : "message"](data.connected ? "Sync attempted" : "Queued — QuickBooks not connected");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const bulkSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/quickbooks/sync", { entity_type: tab, all: true });
      const r = data.results || {};
      toast[data.connected ? "success" : "message"](data.connected
        ? `Processed ${data.processed}: ${r.SYNCED || 0} synced, ${(r.ERROR || 0) + (r.NEEDS_REVIEW || 0)} failed`
        : `Queued ${r.QUEUED || 0} record(s) — QuickBooks not connected`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const retry = async (entity_id) => {
    setBusy(true);
    try { await api.post("/admin/quickbooks/retry", { entity_type: tab, entity_id }); toast.success("Retried"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (summary === false) return <DashboardLayout nav={getNav(user.role)} title="Sync Center"><p>Unable to load.</p></DashboardLayout>;
  const conn = summary?.connection;

  return (
    <DashboardLayout nav={getNav(user.role)} title="QuickBooks Sync Center">
      {!summary ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <span data-testid="qb-sync-conn-badge" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${conn.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{conn.connected ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{conn.connected ? "Connected" : "Not Connected"}</span>
              <span className="text-xs text-slate-500">{conn.environment === "production" ? "Production" : "Sandbox"}{summary.last_sync ? ` · Last sync ${new Date(summary.last_sync).toLocaleString()}` : " · No sync yet"}</span>
            </div>
            <div className="flex items-center gap-2">
              {summary.needs_review_total > 0 && <span data-testid="qb-needs-review-count" className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600"><AlertTriangle className="w-3.5 h-3.5" /> {summary.needs_review_total} need review</span>}
              <Button size="sm" variant="ghost" onClick={refresh} data-testid="qb-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {!conn.connected && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="qb-not-connected-note">
              QuickBooks is not connected. Records can be queued but will only be marked <span className="font-semibold">Synced</span> once QuickBooks confirms them. SJ Property Care operations are never blocked.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ENTITIES.map(({ key, label, icon: Icon }) => {
              const e = summary.entities[key] || { total: 0, counts: {} };
              return (
                <button key={key} data-testid={`qb-tab-${key}`} onClick={() => setTab(key)} className={`text-left bg-white border rounded-xl p-4 transition ${tab === key ? "border-blue-500 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center gap-2 text-slate-500 mb-1.5"><Icon className="w-4 h-4" /><span className="text-xs font-medium">{label}</span></div>
                  <div className="text-2xl font-head font-bold text-slate-900">{e.total}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {e.counts.SYNCED > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{e.counts.SYNCED} synced</span>}
                    {e.counts.QUEUED > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{e.counts.QUEUED} queued</span>}
                    {(e.counts.ERROR > 0 || e.counts.NEEDS_REVIEW > 0) && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{(e.counts.ERROR || 0) + (e.counts.NEEDS_REVIEW || 0)} issues</span>}
                    {e.counts.NOT_SYNCED > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{e.counts.NOT_SYNCED} not synced</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-head font-semibold text-slate-900">{ENTITIES.find((e) => e.key === tab)?.label}</h3>
              <Button size="sm" data-testid="qb-bulk-sync-btn" onClick={bulkSync} disabled={busy} className="bg-blue-600 hover:bg-blue-700"><UploadCloud className="w-4 h-4 mr-1.5" /> {conn.connected ? "Sync All" : "Queue All"}</Button>
            </div>
            {loadingRows ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div> : rows.length === 0 ? (
              <p className="text-sm text-slate-400 p-6">No {ENTITIES.find((e) => e.key === tab)?.label.toLowerCase()} found.</p>
            ) : (
              <div className="divide-y divide-slate-50" data-testid="qb-entity-list">
                {rows.map((r) => (
                  <div key={r.entity_id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{r.name}{r.total != null ? <span className="text-slate-400 font-normal"> · {money(r.total)}</span> : r.amount != null ? <span className="text-slate-400 font-normal"> · {money(r.amount)}</span> : ""}</div>
                      <div className="text-xs text-slate-400 truncate">{r.quickbooks_id ? `QB ID ${r.quickbooks_id}` : r.email || r.entity_id}{r.last_error ? ` · ${r.last_error}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge s={r.sync_status} />
                      {(r.sync_status === "ERROR" || r.sync_status === "NEEDS_REVIEW") ? (
                        <Button size="sm" variant="outline" data-testid={`qb-retry-${r.entity_id}`} onClick={() => retry(r.entity_id)} disabled={busy}>Retry</Button>
                      ) : r.sync_status !== "SYNCED" ? (
                        <Button size="sm" variant="outline" data-testid={`qb-sync-${r.entity_id}`} onClick={() => syncOne(r.entity_id)} disabled={busy}>{conn.connected ? "Sync" : "Queue"}</Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-head font-semibold text-slate-900">Sync Activity Log</h3>
              <span className="text-xs text-slate-400">Auto-queued & manual sync events</span>
            </div>
            {log.length === 0 ? <p className="text-sm text-slate-400 p-6">No sync activity yet.</p> : (
              <div className="divide-y divide-slate-50 max-h-80 overflow-auto" data-testid="qb-log-list">
                {log.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <div className="text-slate-800 truncate"><span className="font-medium capitalize">{l.entity_type?.replace("_", " ")}</span> {l.name ? `· ${l.name}` : `· ${l.entity_id}`}</div>
                      <div className="text-xs text-slate-400 truncate">{l.reason} · {new Date(l.at).toLocaleString()}</div>
                    </div>
                    <StatusBadge s={l.result} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
